import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Languages, Captions, Users, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDur = (secs = 0) => {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}min`;
  if (m > 0) return `${m}min ${String(secs % 60).padStart(2, '0')}s`;
  return `${secs}s`;
};

function LangBars({ rows, light, testid }) {
  const max = Math.max(1, ...rows.map(r => r.seconds));
  if (!rows.length) return <p className={`text-sm ${light ? 'text-gray-400' : 'text-white/40'}`}>Les statistiques se remplissent au fil des lectures.</p>;
  return (
    <div className="space-y-3" data-testid={testid}>
      {rows.map(r => (
        <div key={r.lang}>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className={`font-semibold ${light ? 'text-gray-800' : 'text-white'}`}>{r.lang}</span>
            <span className={light ? 'text-gray-500' : 'text-white/50'}>{fmtDur(r.seconds)} · {r.users} utilisateur{r.users > 1 ? 's' : ''} · {r.contents} contenu{r.contents > 1 ? 's' : ''}</span>
          </div>
          <div className={`h-2 rounded-full overflow-hidden ${light ? 'bg-gray-200' : 'bg-white/10'}`}>
            <div className="h-full rounded-full bg-gradient-to-r from-[#E9641A] to-[#FE975E]" style={{ width: `${(r.seconds / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

const PERIODS = [
  { value: 0, label: 'Tout' },
  { value: 7, label: '7 j' },
  { value: 30, label: '30 j' },
  { value: 90, label: '90 j' },
];

export function LanguageStats({ theme }) {
  const light = theme === 'light';
  const [data, setData] = useState(null);
  const [days, setDays] = useState(0);

  useEffect(() => {
    axios.get(`${API}/admin/analytics/languages`, { params: days ? { days } : {}, withCredentials: true }).then(r => setData(r.data)).catch(() => setData({ by_audio: [], by_subtitle: [], by_content: [], by_user: [] }));
  }, [days]);

  const card = light ? 'bg-white border-gray-200' : 'bg-[#1a0e2e] border-white/5';
  const th = `text-left text-[11px] uppercase tracking-wide font-semibold px-3 py-2 ${light ? 'text-gray-400' : 'text-white/40'}`;
  const td = `px-3 py-2.5 text-sm ${light ? 'text-gray-700' : 'text-white/80'}`;

  if (!data) return null;
  return (
    <div className="space-y-6 mt-8" data-testid="language-stats-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className={`text-lg font-bold flex items-center gap-2 ${light ? 'text-gray-900' : 'text-white'}`}>
          <Languages size={18} className="text-[#E9641A]" /> Langues audio & sous-titres
          {data.total_seconds > 0 && <span className={`text-xs font-normal flex items-center gap-1 ${light ? 'text-gray-500' : 'text-white/50'}`}><Clock size={13} /> {fmtDur(data.total_seconds)} de visionnage suivi</span>}
        </h3>
        <div className={`flex rounded-lg p-0.5 ${light ? 'bg-gray-100' : 'bg-white/5'}`} data-testid="lang-period-filter">
          {PERIODS.map(p => (
            <button
              key={p.value}
              data-testid={`lang-period-${p.value}`}
              onClick={() => setDays(p.value)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                days === p.value
                  ? 'bg-[#E9641A] text-white'
                  : light ? 'text-gray-500 hover:text-gray-800' : 'text-white/40 hover:text-white'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className={`rounded-xl border p-5 ${card}`} data-testid="lang-audio-card">
          <p className={`text-sm font-bold mb-4 flex items-center gap-2 ${light ? 'text-gray-800' : 'text-white'}`}><Languages size={15} className="text-[#E9641A]" /> Langues audio les plus écoutées</p>
          <LangBars rows={data.by_audio} light={light} testid="lang-audio-bars" />
        </div>
        <div className={`rounded-xl border p-5 ${card}`} data-testid="lang-subs-card">
          <p className={`text-sm font-bold mb-4 flex items-center gap-2 ${light ? 'text-gray-800' : 'text-white'}`}><Captions size={15} className="text-[#E9641A]" /> Sous-titres les plus utilisés</p>
          <LangBars rows={data.by_subtitle} light={light} testid="lang-subs-bars" />
        </div>
      </div>

      {data.by_content.length > 0 && (
        <div className={`rounded-xl border p-5 ${card}`} data-testid="lang-content-card">
          <p className={`text-sm font-bold mb-4 ${light ? 'text-gray-800' : 'text-white'}`}>Par contenu — langues & sous-titres regardés</p>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className={light ? 'border-b border-gray-200' : 'border-b border-white/10'}>
                <th className={th}>Contenu</th><th className={th}>Durée totale</th><th className={th}>Langues audio</th><th className={th}>Sous-titres</th>
              </tr></thead>
              <tbody>
                {data.by_content.map(c => (
                  <tr key={c.content_id} className={light ? 'border-b border-gray-100' : 'border-b border-white/5'}>
                    <td className={`${td} font-semibold`}>{c.title}</td>
                    <td className={td}>{fmtDur(c.seconds)}</td>
                    <td className={td}>
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(c.audio).sort((a, b) => b[1].seconds - a[1].seconds).map(([l, v]) => (
                          <span key={l} className={`text-[11px] px-2 py-0.5 rounded-full border ${light ? 'bg-orange-50 border-orange-200 text-orange-700' : 'bg-[#E9641A]/10 border-[#E9641A]/30 text-[#FE975E]'}`}>{l} · {fmtDur(v.seconds)} · {v.users} 👤</span>
                        ))}
                      </div>
                    </td>
                    <td className={td}>
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(c.subs).sort((a, b) => b[1].seconds - a[1].seconds).map(([l, v]) => (
                          <span key={l} className={`text-[11px] px-2 py-0.5 rounded-full border ${light ? 'bg-gray-50 border-gray-200 text-gray-600' : 'bg-white/5 border-white/15 text-white/60'}`}>{l} · {fmtDur(v.seconds)}</span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data.by_user.length > 0 && (
        <div className={`rounded-xl border p-5 ${card}`} data-testid="lang-user-card">
          <p className={`text-sm font-bold mb-4 flex items-center gap-2 ${light ? 'text-gray-800' : 'text-white'}`}><Users size={15} className="text-[#E9641A]" /> Par utilisateur — préférences de langue</p>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className={light ? 'border-b border-gray-200' : 'border-b border-white/10'}>
                <th className={th}>Utilisateur</th><th className={th}>Langue principale</th><th className={th}>Sous-titres</th><th className={th}>Durée écoutée</th><th className={th}>Détail langues</th>
              </tr></thead>
              <tbody>
                {data.by_user.map(u => (
                  <tr key={u.email} className={light ? 'border-b border-gray-100' : 'border-b border-white/5'}>
                    <td className={`${td} font-semibold`}>{u.email}</td>
                    <td className={td}>{u.top_audio}</td>
                    <td className={td}>{u.top_sub}</td>
                    <td className={td}>{fmtDur(u.seconds)}</td>
                    <td className={td}>
                      <div className="flex flex-wrap gap-1.5">
                        {u.audio_detail.map(d => (
                          <span key={d.lang} className={`text-[11px] px-2 py-0.5 rounded-full border ${light ? 'bg-orange-50 border-orange-200 text-orange-700' : 'bg-[#E9641A]/10 border-[#E9641A]/30 text-[#FE975E]'}`}>{d.lang} · {fmtDur(d.seconds)}</span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
